/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { type CopilotState } from '../data-layer/types';
import {
  removeAddAccessEntitlementTemplate,
  setAddAccessEntitlementTemplates,
  setFormStatus,
  setSavedStepData,
  updateEntitlements,
  updateStepFormData,
} from '../data-store/copilotSlice';

import { useApplicationName } from './index';

export interface Entitlement {
  id: number;
  value: string;
}

export const useAddAccessLogic = (
  handleFormSubmit: (
    stepName: string,
    formData: Record<string, unknown>,
    isValid: boolean
  ) => void
) => {
  const dispatch = useDispatch();

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const applicationName: string = useApplicationName();

  // Get saved data from Redux
  const savedData = useSelector(
    (state: { copilot: CopilotState }) =>
      state.copilot.stepFormData.AddAccess || {}
  );

  const isEditMode = useSelector(
    (state: { copilot: CopilotState }) => state.copilot.ui.mode === 'edit'
  );

  const addAccessTemplateData = useSelector(
    (state: { copilot: CopilotState }) =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      state.copilot.parsedTemplates.AddAccess || {}
  );

  // Use Redux entitlements directly with useMemo to prevent dependency changes
  const entitlements: Entitlement[] = useMemo(() => {
    // In edit mode, check if we have API data to load
    // Always prioritize Redux data (user changes) first
    if (
      savedData.entitlements &&
      (savedData.entitlements as Entitlement[]).length > 0
    ) {
      return savedData.entitlements as Entitlement[];
    }

    // In edit mode, populate from API data only if no Redux data exists
    if (isEditMode && addAccessTemplateData?.attributeValue) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        const attributeValue = JSON.parse(addAccessTemplateData.attributeValue);
        const addAccessTemplates: any[] = [];

        const savedEntitlement = Array.isArray(attributeValue?.call)
          ? // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            attributeValue.call.map((attr: { name: unknown }) => {
              addAccessTemplates.push(attr);

              return { id: Math.random(), value: attr.name };
            })
          : [{ id: Date.now(), value: '' }];

        // Store addAccessTemplates in Redux
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        dispatch(setAddAccessEntitlementTemplates(addAccessTemplates));

        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return savedEntitlement;
      } catch (error) {
        return [{ id: Date.now(), value: '' }];
      }
    }

    // Default fallback
    return [{ id: Date.now(), value: '' }];
  }, [
    savedData.entitlements,
    isEditMode,
    addAccessTemplateData.attributeValue,
    dispatch,
  ]);

  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isSkipped, setIsSkipped] = useState<boolean>(false);

  // Sync local state with Redux on mount to handle reloads
  const skippedSteps = useSelector(
    (state: { copilot: CopilotState }) => state.copilot.navigation.skippedSteps
  );

  // Initialize isSkipped from Redux if form was previously skipped
  useMemo(() => {
    if (skippedSteps.includes('AddAccess')) {
      setIsSkipped(true);
    }
  }, [skippedSteps]);

  const MAX_ENTITLEMENTS = 6;

  const handleChange = useCallback(
    (index: number, value: string): void => {
      const updated = [...entitlements];

      updated[index] = { ...updated[index], value };

      // Store in both locations
      dispatch(
        updateStepFormData({
          stepName: 'AddAccess',
          formData: { entitlements: updated },
        })
      );
      // Use updateEntitlements to preserve iteration state
      dispatch(updateEntitlements(updated));
    },
    [dispatch, entitlements]
  );

  const addEntitlement = useCallback((): void => {
    if (entitlements.length >= MAX_ENTITLEMENTS) {
      setIsVisible(true);

      return;
    }

    const newEntitlements = [...entitlements, { id: Date.now(), value: '' }];

    // Store in both locations
    dispatch(
      updateStepFormData({
        stepName: 'AddAccess',
        formData: { entitlements: newEntitlements },
      })
    );
    // Use updateEntitlements to preserve iteration state
    dispatch(updateEntitlements(newEntitlements));
  }, [entitlements, MAX_ENTITLEMENTS, dispatch]);

  const removeEntitlement = useCallback(
    (index: number): void => {
      const updated = entitlements.filter((_, i) => i !== index);

      if (updated.length <= MAX_ENTITLEMENTS) {
        setIsVisible(false);
      }

      // Store in both locations
      dispatch(
        updateStepFormData({
          stepName: 'AddAccess',
          formData: { entitlements: updated },
        })
      );
      // Use updateEntitlements to preserve iteration state
      dispatch(updateEntitlements(updated));
    },
    [entitlements, MAX_ENTITLEMENTS, dispatch]
  );

  const handleAddEntitlement = useCallback((): void => {
    addEntitlement();
  }, [addEntitlement]);

  const handleRemoveEntitlement = useCallback(
    (index: number): void => {
      removeEntitlement(index);

      // Also remove the corresponding template from addAccessTemplates
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      dispatch(removeAddAccessEntitlementTemplate(index));
    },
    [removeEntitlement, dispatch]
  );

  const handleSave = useCallback((): boolean => {
    const validEntitlements = entitlements.filter((ent) => ent.value.trim());
    const isValid = validEntitlements.length > 0;

    if (isValid) {
      // Collapse the AddAccess input form after completion
      dispatch(
        setFormStatus({
          stepName: 'AddAccess',
          formStatus: 'collapsedview',
        })
      );

      // Save to savedStepData on successful validation
      dispatch(
        setSavedStepData({
          stepName: 'AddAccess',
          formData: { entitlements: validEntitlements },
        })
      );

      handleFormSubmit(
        'SetEntitlementSteps',
        { entitlements: validEntitlements },
        true
      );

      return true;
    }

    return false;
  }, [dispatch, entitlements, handleFormSubmit]);

  const onCancel = useCallback((): void => {
    setIsVisible(false);
  }, []);

  const hasValidEntitlements: boolean = entitlements.some((ent) =>
    ent.value.trim()
  );

  return {
    entitlements,
    applicationName,
    isVisible,
    isSkipped,
    hasValidEntitlements,
    handleChange,
    handleAddEntitlement,
    handleRemoveEntitlement,
    handleSave,
    setIsSkipped,
    onCancel,
  };
};
